import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { AdminCatalogProduct } from "../../api/fetchCatalogProducts";
import { fetchCatalogProduct } from "../../api/fetchCatalogProduct";
import {
  SaveCatalogProductRequest,
  updateCatalogProduct,
} from "../../api/updateCatalogProduct";
import CatalogProductForm from "../CatalogProductForm/CatalogProductForm";

export default function CatalogProductEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [product, setProduct] = useState<AdminCatalogProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    // Reset for id changes so edit A -> edit B never shows A's data in a
    // form that would save under B's id.
    setProduct(null);
    setLoading(true);
    setLoadFailed(false);
    fetchCatalogProduct(id, controller.signal)
      .then((response) => {
        setProduct(response.data);
        setLoading(false);
      })
      .catch(() => {
        // A failed load must never fall through to an empty editable form —
        // saving it would overwrite the live record with blanks.
        if (!controller.signal.aborted) {
          setLoadFailed(true);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [id]);

  const handleSubmit = async (data: SaveCatalogProductRequest) => {
    if (!id) return;
    setIsSaving(true);
    await updateCatalogProduct(id, data)
      .then(() => {
        toast({
          title: "Success",
          description: "Catalog product updated",
          variant: "default",
        });
        navigate("/admin/product-catalog/products");
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <Link
        className="inline-flex items-center gap-2 mb-4 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        to="/admin/product-catalog/products"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Catalog Products
      </Link>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Edit Catalog Product
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Update this entry in the member-facing Product Catalog.
        </p>
      </div>
      {loading ? (
        <LoadingIndicator />
      ) : loadFailed || !product ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This catalog product couldn&apos;t be loaded. Go back to the list and
          try again.
        </p>
      ) : (
        <CatalogProductForm
          initialData={product}
          isLoading={isSaving}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
